import React from 'react';
import {
    wallboardTypes,
    DATA_PERIOD_TYPE_DAILY,
} from 'flow/utils/wallboard-utils';

import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    dataPeriodHours: number;
    dataPeriodType: string;
    serviceLevelPeriod: number;
    serviceLevelWarningThreshold: number;
    onNavigateToCustomizeSettings: () => void;
    onNavigateToVisibilitySettings: () => void;
    boardType: 'hunt_groups_multiple' | 'team_custom';
}

const DashboardSettingsList = ({
    dataPeriodHours,
    dataPeriodType,
    serviceLevelPeriod,
    serviceLevelWarningThreshold,
    onNavigateToCustomizeSettings,
    onNavigateToVisibilitySettings,
    boardType,
}: Props) => {
    const { i18next } = useAppContext();

    const dataPeriodItem = () => {
        const dataPeriodText =
            dataPeriodType === DATA_PERIOD_TYPE_DAILY
                ? i18next.t('wallboard.settings.dataperiod.daily')
                : `${dataPeriodHours} ${i18next.t('duration.hours')}`;

        return (
            <React.Fragment>
                <div key={'dataperiod'}>
                    <div className="flex items-center justify-between ps-3 text-sm">
                        {i18next.t('wallboard.settings.dataperiod')}

                        <div className="text-gray-500">{dataPeriodText}</div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderServiceLevel = () => {
        const serviceLevelWarningText =
            serviceLevelWarningThreshold || serviceLevelWarningThreshold === 0
                ? `${serviceLevelWarningThreshold}%`
                : '-';

        return (
            <React.Fragment>
                <Divider />
                <div key={'servicelevelperiod'}>
                    <div className="flex items-center justify-between ps-3 text-sm">
                        {i18next.t('wallboard.settings.servicelevelperiod')}
                        <div className="text-gray-500">
                            {`${serviceLevelPeriod} ${i18next.t(
                                'duration.seconds',
                            )}`}
                        </div>
                    </div>
                </div>
                <Divider />
                <div key={'servicelevelwarning'}>
                    <div className="flex items-center justify-between ps-3 text-sm">
                        {i18next.t('wallboard.settings.servicelevelwarning')}

                        <div className="text-gray-500">
                            {serviceLevelWarningText}
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const renderAdvancedSection = () => {
        return (
            <React.Fragment>
                <div className="mt-3 mb-1.5 ps-3 text-xs font-bold">
                    {i18next.t('settings.advanced')}
                </div>
                <Divider className="mt-1 mb-0" />
                <div
                    onClick={onNavigateToVisibilitySettings}
                    key={'visibility'}
                >
                    <div className="flex cursor-pointer items-center justify-between py-6 ps-3 text-sm hover:bg-gray-100">
                        {i18next.t('wallboard.sharing')}
                        <Icon
                            iconKey="chevron_right"
                            className="rtl:scale-x-[-1]"
                        />
                    </div>
                </div>
                <Divider className="mt-0 mb-0" />
            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            <div className="mt-3 mb-1.5 ps-3 text-xs font-bold">
                {i18next.t('wallboard.description.settings')}
            </div>
            <Divider className="mt-1" />
            {dataPeriodItem()}
            {boardType === wallboardTypes.HUNT_GROUPS_MULTIPLE
                ? renderServiceLevel()
                : null}
            <Divider className="mb-0" />
            <div
                onClick={onNavigateToCustomizeSettings}
                key={'customizesettings'}
            >
                <div className="flex cursor-pointer items-center justify-between py-6 ps-3 text-sm hover:bg-gray-100">
                    {i18next.t('wallboard.settings.customize')}
                    <Icon
                        iconKey="chevron_right"
                        className="rtl:scale-x-[-1]"
                    />
                </div>
            </div>

            <Divider className="mt-0" />
            {onNavigateToVisibilitySettings ? renderAdvancedSection() : null}
        </React.Fragment>
    );
};

export default DashboardSettingsList;
